import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const ProfileModal = ({ isOpen, onClose, user }) => {
  const { instance, accounts } = useMsal();
  const [funPoints, setFunPoints] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    if (!isOpen || !instance || !accounts?.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [meRes, boardRes] = await Promise.all([
          authenticatedApiCall(instance, accounts, 'GET', '/learning-fun/me'),
          authenticatedApiCall(instance, accounts, 'GET', '/learning-fun/leaderboard?limit=12')
        ]);
        if (cancelled) return;
        setFunPoints(meRes?.data || null);
        setLeaderboard(boardRes?.data?.leaderboard || []);
      } catch {
        if (!cancelled) {
          setFunPoints(null);
          setLeaderboard([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, instance, accounts]);

  if (!isOpen) return null;

  const fields = [
    { label: 'Name', value: user?.display_name || user?.name || 'N/A' },
    { label: 'Email', value: user?.email || 'N/A' },
    { label: 'Role', value: user?.role_display_name || user?.role_name || 'N/A' },
    { label: 'Department', value: user?.role_department_name || 'N/A' },
    { label: 'Authority Level', value: user?.effective_authority_level || user?.user_authority_level || user?.role_default_authority || 'N/A' },
  ];

  const getInitials = () => {
    const name = user?.display_name || user?.name || '';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0]?.substring(0, 2).toUpperCase() || 'U';
  };

  const guideLabel = (id) => {
    if (id === 'evm-report-help') return 'EVM / Resource Control';
    if (id === 'budget-report-help') return 'Budget Hours Baseline';
    return id;
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', width: '520px',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
      }}>
        <div style={{
          padding: '24px', borderBottom: '1px solid #e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #dc3545 0%, #b91c1c 100%)',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px', fontWeight: '700', letterSpacing: '1px',
              border: '2px solid rgba(255,255,255,0.4)'
            }}>
              {getInitials()}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {user?.display_name || user?.name || 'User'}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
                {user?.email || ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', fontSize: '20px',
            cursor: 'pointer', color: 'white', padding: '4px 10px',
            lineHeight: 1, borderRadius: '6px'
          }}>&times;</button>
        </div>

        <div style={{ padding: '8px 0', overflowY: 'auto', flex: 1 }}>
          {fields.map((field, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', padding: '14px 24px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{
                width: '140px', flexShrink: 0,
                fontSize: '13px', fontWeight: '600', color: '#6b7280',
                textTransform: 'uppercase', letterSpacing: '0.5px'
              }}>
                {field.label}
              </div>
              <div style={{
                flex: 1, fontSize: '15px', color: '#1f2937', fontWeight: '500'
              }}>
                {field.value}
              </div>
            </div>
          ))}

          <div style={{ padding: '16px 24px 8px' }}>
            <div style={{
              fontSize: '13px', fontWeight: '700', color: '#b91c1c',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8
            }}>
              Fun learning points
            </div>
            <div style={{
              fontSize: '12px', color: '#64748b', lineHeight: 1.45, marginBottom: 10,
              padding: '8px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8
            }}>
              {funPoints?.disclaimer
                || 'These points are only for fun — to make learning the portal more enjoyable. They do not affect your role, permissions, pay, or performance reviews.'}
            </div>
            {(funPoints?.rules || []).length > 0 && (
              <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: '12px', color: '#475569', lineHeight: 1.45 }}>
                {funPoints.rules.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
              {funPoints?.totalPoints != null ? funPoints.totalPoints : '—'}
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#64748b', marginLeft: 6 }}>quiz points</span>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 10 }}>
              From Help “Test your knowledge” only — not from time spent in the portal.
            </div>
            {(funPoints?.byGuide || []).length > 0 ? (
              <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
                {funPoints.byGuide.map((g) => (
                  <div key={g.guideId} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontSize: '13px', color: '#334155', padding: '6px 0',
                    borderBottom: '1px solid #f1f5f9'
                  }}>
                    <span>{guideLabel(g.guideId)}</span>
                    <span style={{ fontWeight: 600 }}>{g.points} pts · best {g.bestScore}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: 14 }}>
                Take a “Test your knowledge” quiz at the end of a Help guide to earn fun points.
              </div>
            )}

            <div style={{
              fontSize: '13px', fontWeight: '700', color: '#b91c1c',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8
            }}>
              Fun leaderboard
            </div>
            {leaderboard.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>No scores yet — be the first.</div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                {leaderboard.map((row) => (
                  <div key={row.userId} style={{
                    display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 8,
                    alignItems: 'center', fontSize: '13px', color: '#334155',
                    padding: '6px 0', borderBottom: '1px solid #f1f5f9'
                  }}>
                    <span style={{ fontWeight: 700, color: '#94a3b8' }}>#{row.rank}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.displayName}
                    </span>
                    <span style={{ fontWeight: 700 }}>{row.totalPoints}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{
          padding: '14px 24px', backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          fontSize: '12px', color: '#6b7280', textAlign: 'center'
        }}>
          Profile information is managed through your organization&apos;s Azure Active Directory.
        </div>

        <div style={{
          padding: '14px 24px', borderTop: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'flex-end'
        }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', backgroundColor: '#6c757d', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
          }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
